import React from 'react';
import { useSelector } from 'react-redux';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Link } from 'react-router-dom';
import { useParamReplace, useProjectRoomsEnabled, useUnreadThreadCount } from '@/utils/hooks';
import { SimplePageTitle } from '@/components/layout/PageTitle';
import ResidentialUnitsSorted from './ResidentialUnitsSorted';
import { CommentThreadType } from '@/types';
import ResidentialUnitsGrouped from './ResidentialUnitsGrouped';
import { Tabs } from '@ygtechnologies/rubus-ui';
import { ProjectRooms } from '../ProjectRooms';
import { IState } from '@/state/IState';

export enum ResidentialUnitsTab {
    All = 'all',
    ByLastMessage = 'by-last-message',
    Messages = 'messages',
    Rooms = 'rooms',
}

interface ProjectResidentialUnitsProps {
    tab: ResidentialUnitsTab;
}

const ProjectResidentialUnits: React.FC<ProjectResidentialUnitsProps> = ({ tab = ResidentialUnitsTab.All }) => {
    const generateUrl = useParamReplace<{ tab?: ResidentialUnitsTab }>(
        `/projects/:projectId/residential-units/:tab(by-last-message|rooms)?/:viewMode(compressed|expanded)?`
    );
    const generateRoomUrl = useParamReplace(`/projects/:projectId/residential-units/rooms/`);

    const unreadMessages = useUnreadThreadCount();
    const isAdministrator = useSelector(
        (state: IState) => state.global.activeProject?.permissions?.isAdministrator ?? false
    );
    const roomsEnabled = useProjectRoomsEnabled();
    const showRooms = isAdministrator && roomsEnabled;

    const renderContent = () => {
        switch (tab) {
            case ResidentialUnitsTab.All:
                return <ResidentialUnitsGrouped />;
            case ResidentialUnitsTab.ByLastMessage:
                return <ResidentialUnitsSorted />;
            case ResidentialUnitsTab.Rooms:
                return showRooms ? <ProjectRooms /> : null;
            default:
                return null;
        }
    };

    return (
        <>
            <SimplePageTitle title={t`Units`} />
            <Tabs.Container activeId={tab} variant="underline" sticky stickyClassName="stickyBleed">
                <Tabs.Tab
                    id={ResidentialUnitsTab.All}
                    as={Link}
                    to={generateUrl({ tab: undefined })}
                    label={<Trans>All units</Trans>}
                />
                <Tabs.Tab
                    id={ResidentialUnitsTab.ByLastMessage}
                    as={Link}
                    to={generateUrl({ tab: ResidentialUnitsTab.ByLastMessage })}
                    label={<Trans>By last message</Trans>}
                    badge={unreadMessages.total - unreadMessages[CommentThreadType.Project]}
                />
                {showRooms && (
                    <Tabs.Tab
                        id={ResidentialUnitsTab.Rooms}
                        as={Link}
                        to={generateRoomUrl({})}
                        label={<Trans>Rooms</Trans>}
                    />
                )}
                <Tabs.Content>{renderContent()}</Tabs.Content>
            </Tabs.Container>
        </>
    );
};
export default ProjectResidentialUnits;
